export type VisibilityMode = 'inclusion' | 'exclusion'

export interface EvaluateVisibilityRulesOptions {
  countries?: string[]
  languages?: string[]
  countryMode?: string | null
  languageMode?: string | null
  countryCookie?: string | null
  locale?: string | null
  debug?: boolean
}

/**
 * `pass`: true if allowed, null if undecided (e.g. no country cookie),
 * false if blocked (with every applicable reason).
 */
export interface VisibilityRulesResult {
  pass: boolean | null
  reasons: Array<'country' | 'language'>
}

export function evaluateVisibilityRulesDetailed(
  options: EvaluateVisibilityRulesOptions,
): VisibilityRulesResult {
  const countries = options.countries || []
  const languages = options.languages || []
  const countryMode = options.countryMode as VisibilityMode | undefined
  const languageMode = options.languageMode as VisibilityMode | undefined
  const countryCookie = options.countryCookie?.trim?.() || ''
  const locale = options.locale?.trim?.() || ''
  const debugLog = (step: string, details: Record<string, unknown>) => {
    if (options.debug) console.log(`[visibilityRules] ${step}`, details)
  }

  debugLog('configured rules', {
    countries,
    languages,
    countryMode,
    languageMode,
  })

  debugLog('visitor context', {
    countryCookie,
    locale,
  })

  const needsCountry =
    countries.length > 0 &&
    (countryMode === 'inclusion' || countryMode === 'exclusion')

  const countryUnknown = needsCountry && !countryCookie

  let passCountries = true
  if (!countryUnknown && countries.length && countryMode === 'inclusion') {
    passCountries = countries.includes(countryCookie)
  } else if (
    !countryUnknown &&
    countries.length &&
    countryMode === 'exclusion'
  ) {
    passCountries = !countries.includes(countryCookie)
  }

  debugLog('country', {
    required: needsCountry,
    unknown: countryUnknown,
    passed: passCountries,
  })

  let passLanguages = true
  if (languages.length && languageMode === 'inclusion') {
    passLanguages = languages.includes(locale)
  } else if (languages.length && languageMode === 'exclusion') {
    passLanguages = !languages.includes(locale)
  }

  debugLog('language', {
    required:
      languages.length > 0 &&
      (languageMode === 'inclusion' || languageMode === 'exclusion'),
    passed: passLanguages,
  })

  const reasons: VisibilityRulesResult['reasons'] = []
  if (!passCountries) reasons.push('country')
  if (!passLanguages) reasons.push('language')

  const result: VisibilityRulesResult =
    reasons.length > 0
      ? { pass: false, reasons }
      : { pass: countryUnknown ? null : true, reasons }
  debugLog('decision', { result })
  return result
}

export function evaluateVisibilityRules(
  options: EvaluateVisibilityRulesOptions,
): boolean | null {
  return evaluateVisibilityRulesDetailed(options).pass
}
